import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Lock, Shield, Users, Server } from 'lucide-react';
import { PublicPageLayout } from '@/components/landing/PublicPageLayout';
import { SEO } from '@/components/SEO';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] as const } 
  }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } }
};

const securityCards = [
  {
    icon: Lock,
    title: 'Encryption at Rest & in Transit',
    body: 'All data is encrypted using AES-256 at rest and TLS 1.2+ in transit. Nothing is stored in plaintext.',
  },
  {
    icon: Shield,
    title: 'Complete Data Isolation',
    body: "Each organisation's data is fully isolated. Your content, proposals, and analytics are never shared or used to train AI models.",
  },
  {
    icon: Users,
    title: 'Role-Based Access Controls',
    body: 'Granular permissions ensure users only access what they need. Admins control viewing, editing, and exporting across all proposal content.',
  },
  {
    icon: Server,
    title: 'Secure Cloud Infrastructure',
    body: 'Hosted on enterprise-grade cloud infrastructure with automated backups, continuous monitoring, and 99.9% uptime SLA.',
  },
];

export default function Security() {
  return (
    <PublicPageLayout>
      <SEO 
        title="Security & Compliance | Sorvaine"
        description="Sorvaine is built on enterprise-grade security. SOC 2 aligned, GDPR compliant, and designed for sensitive procurement data."
        canonical="https://sorvaine.com/security"
      />

      {/* Hero Section */}
      <section className="min-h-[60vh] flex items-center pt-20 pb-12 md:pt-28 md:pb-20 relative">
        <div className="absolute inset-0 geometric-grid opacity-30 pointer-events-none" />

        <div className="max-w-[1200px] mx-auto px-6 w-full relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.div variants={fadeIn} className="flex items-center gap-4 mb-6 md:mb-12">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <div className="w-10 h-px bg-gradient-to-r from-primary to-transparent" />
              </div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-medium">Security</span>
            </motion.div>

            <motion.h1
              variants={fadeIn}
              className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] tracking-tight leading-[0.95] mb-5 md:mb-8"
            >
              Your proposal data is secure by&nbsp;design.
            </motion.h1>
            
            <motion.p 
              variants={fadeIn}
              className="text-muted-foreground text-lg md:text-xl leading-relaxed max-w-[52ch]"
              style={{ lineHeight: '1.7' }}
            >
              Sorvaine is built with enterprise-grade security from the ground up. Your proposal data is sensitive — and protected at every layer.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="divider-glow" />
      </div>

      {/* Security Cards — 2×2 Grid */}
      <section className="py-28 md:py-36 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="grid md:grid-cols-2 gap-6"
          >
            {securityCards.map((card) => (
              <motion.div
                key={card.title}
                variants={fadeIn}
                className="rounded-md border border-border bg-card p-8 md:p-10 space-y-5"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <card.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl tracking-tight">{card.title}</h3>
                </div>
                <p className="text-muted-foreground text-base leading-[1.8]">{card.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="divider-glow" />
      </div>

      {/* Callout */}
      <section className="py-28 md:py-36">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center"
          >
            <p className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.2]">
              Your proposal data is never used to train AI models.
            </p>
            <p className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mt-4 text-primary font-bold">
              Ever.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="divider-glow" />
      </div>

      {/* Questions / CTA */}
      <section className="pt-16 pb-28 md:pt-20 md:pb-36 relative">
        <div className="max-w-[1200px] mx-auto px-6 text-center relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="space-y-8"
          >
            <motion.div variants={fadeIn} className="flex items-center justify-center gap-4 mb-4">
              <span className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground font-medium">Questions</span>
            </motion.div>

            <motion.h2 
              variants={fadeIn}
              className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight"
            >
              Have a security question?
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-muted-foreground text-lg max-w-lg mx-auto"
            >
              We can provide additional security documentation and answer any questions for your team.
            </motion.p>
            <motion.div variants={fadeIn}>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-md bg-transparent border border-primary hover:bg-primary/10 hover:-translate-y-[1px] hover:scale-[1.03] transition-all duration-200 cursor-pointer"
              >
                <span className="text-[15px] font-semibold tracking-[0.01em] text-primary">Contact Us</span>
                <ArrowRight className="w-4 h-4 text-primary" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </PublicPageLayout>
  );
}
